import React, { useRef, useEffect, useState } from 'react';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Camera } from 'lucide-react';
import {
  formatTime,
  Caption,
  CaptionsSettings,
  CanvasAspect,
  ClipFit,
  ClipMotion,
} from '../../types/editor';

interface Props {
  blobUrl: string;
  mediaKind?: 'video' | 'image';
  canvasAspect?: CanvasAspect;
  fit?: ClipFit;
  motion?: ClipMotion;
  brightness?: number;
  segmentDuration?: number;
  captions?: Caption[];
  captionsSettings?: CaptionsSettings;
  segmentStartTime?: number;
  segmentSpeed?: number;
  segmentVolume?: number;
  segmentMuted?: boolean;
  currentTime: number;
  isPlaying: boolean;
  totalDuration: number;
  onTimeUpdate: (time: number) => void;
  onPlayPause: () => void;
  onSeek: (time: number) => void;
  onCaptureFrame: (b64: string) => void;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  /** Voz / som / extra tracks mixed on top of the clip audio. */
  overlayAudio?: Array<{
    id: string;
    url: string;
    startOffset: number;
    duration: number;
    volume: number;
    muted?: boolean;
    trimStart?: number;
    trimEnd?: number;
    loop?: boolean;
    speed?: number;
  }>;
}

export default function PreviewPlayer({
  blobUrl,
  mediaKind = 'video',
  canvasAspect = '16:9',
  fit = 'cover',
  motion = 'none',
  brightness = 1,
  segmentDuration = 0,
  segmentStartTime = 0,
  segmentSpeed = 1,
  segmentVolume = 1,
  segmentMuted = false,
  currentTime,
  isPlaying,
  totalDuration,
  captions = [],
  captionsSettings,
  onTimeUpdate,
  onPlayPause,
  onSeek,
  onCaptureFrame,
  videoRef,
  overlayAudio = [],
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const overlayEls = useRef<Map<string, HTMLAudioElement>>(new Map());
  const currentTimeRef = useRef(currentTime);
  currentTimeRef.current = currentTime;
  const [localMuted, setLocalMuted] = useState(false);
  const [localVolume, setLocalVolume] = useState(1);
  const [stageBox, setStageBox] = useState({ w: 0, h: 0 });
  const isImage = mediaKind === 'image';

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const apply = () => {
      const r = el.getBoundingClientRect();
      setStageBox({ w: r.width, h: r.height });
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const vid = videoRef.current;
    if (vid) {
      vid.volume = (segmentMuted || localMuted) ? 0 : (segmentVolume * localVolume);
    }
  }, [segmentVolume, segmentMuted, localVolume, localMuted, videoRef, blobUrl]);

  useEffect(() => {
    const map = overlayEls.current;
    const nextIds = new Set(overlayAudio.map((t) => t.id));
    Array.from(map.entries()).forEach(([id, el]) => {
      if (nextIds.has(id)) return;
      el.pause();
      el.removeAttribute('src');
      el.load();
      map.delete(id);
    });
    for (const track of overlayAudio) {
      let el = map.get(track.id);
      if (!el) {
        el = new Audio();
        el.preload = 'auto';
        map.set(track.id, el);
      }
      if (el.src !== track.url) el.src = track.url;
    }
  }, [overlayAudio]);

  useEffect(() => {
    const map = overlayEls.current;
    return () => {
      Array.from(map.values()).forEach((el) => {
        el.pause();
        el.removeAttribute('src');
        el.load();
      });
      map.clear();
    };
  }, []);

  useEffect(() => {
    if (!isPlaying) return;
    Array.from(overlayEls.current.values()).forEach((el) => {
      const wasMuted = el.muted;
      el.muted = true;
      void el.play().then(() => {
        el.pause();
        el.muted = wasMuted;
      }).catch(() => {
        el.muted = wasMuted;
      });
    });
  }, [isPlaying]);

  useEffect(() => {
    for (const track of overlayAudio) {
      const el = overlayEls.current.get(track.id);
      if (!el) continue;
      const trimStart = track.trimStart || 0;
      const trimEnd = track.trimEnd || 0;
      const rate = track.speed && track.speed > 0 ? track.speed : 1;
      const sourceLen = Math.max(0.05, track.duration - trimStart - trimEnd);
      const wallSpan = sourceLen / rate;
      const start = track.startOffset;
      const end = track.loop ? totalDuration : start + wallSpan;
      const inRange = currentTime >= start && currentTime < end - 0.02;
      const trackMuted = Boolean(track.muted) || localMuted;
      el.volume = trackMuted ? 0 : Math.min(1, Math.max(0, track.volume * localVolume));
      el.muted = trackMuted;
      el.playbackRate = rate;
      if (!isPlaying || !inRange || track.muted) {
        if (!el.paused) el.pause();
        continue;
      }
      const wall = currentTime - start;
      const local = track.loop
        ? trimStart + ((wall * rate) % sourceLen)
        : trimStart + wall * rate;
      if (Number.isFinite(local) && Math.abs((el.currentTime || 0) - local) > 0.2) {
        try { el.currentTime = Math.max(0, local); } catch { /* seek may fail before metadata */ }
      }
      if (el.paused) {
        void el.play().catch((err) => console.warn('Áudio overlay play:', err));
      }
    }
  }, [overlayAudio, isPlaying, currentTime, localMuted, localVolume, totalDuration]);

  useEffect(() => {
    const vid = videoRef.current;
    if (!vid || isImage) return;
    if (isPlaying && vid.paused) {
      vid.play().catch((e) => console.warn('Play interrupted:', e));
    } else if (!isPlaying && !vid.paused) {
      vid.pause();
    }
  }, [isPlaying, blobUrl, videoRef, isImage]);

  useEffect(() => {
    const vid = videoRef.current;
    if (!vid || isImage) return;
    const expectedLocal = (currentTime - segmentStartTime) * segmentSpeed;
    if (Math.abs(vid.currentTime - expectedLocal) > 0.15) {
      vid.currentTime = Math.max(0, expectedLocal);
    }
  }, [currentTime, segmentStartTime, segmentSpeed, blobUrl, videoRef, isImage]);

  useEffect(() => {
    const vid = videoRef.current;
    if (!vid || isImage) return;
    const handleTimeUpdate = () => {
      if (vid.paused) return;
      onTimeUpdate(segmentStartTime + (vid.currentTime / segmentSpeed));
    };
    vid.addEventListener('timeupdate', handleTimeUpdate);
    return () => vid.removeEventListener('timeupdate', handleTimeUpdate);
  }, [videoRef, onTimeUpdate, segmentStartTime, segmentSpeed, isImage]);

  useEffect(() => {
    if (!isImage || !isPlaying) return;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = (now - last) / 1000;
      last = now;
      const next = currentTimeRef.current + dt;
      if (next >= totalDuration) {
        onTimeUpdate(totalDuration);
        onPlayPause();
        return;
      }
      onTimeUpdate(next);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isImage, isPlaying, blobUrl, totalDuration, onTimeUpdate, onPlayPause]);

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSeek(parseFloat(e.target.value));
  };

  const handleFrameStep = (direction: -1 | 1) => {
    onSeek(Math.max(0, Math.min(totalDuration, currentTime + (direction * (1 / 30)))));
  };

  const handleCapture = () => {
    const cvs = canvasRef.current;
    const vid = videoRef.current;
    const img = imgRef.current;
    if (!cvs) return;
    const src = isImage ? img : vid;
    if (!src) return;
    const w = isImage
      ? (img?.naturalWidth || 1280)
      : (vid?.videoWidth || 1280);
    const h = isImage
      ? (img?.naturalHeight || 720)
      : (vid?.videoHeight || 720);
    cvs.width = w;
    cvs.height = h;
    const ctx = cvs.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(src, 0, 0, w, h);
    onCaptureFrame(cvs.toDataURL('image/jpeg', 0.85));
  };

  const localT = Math.max(0, currentTime - segmentStartTime);
  const p = segmentDuration > 0 ? Math.min(1, localT / segmentDuration) : 0;
  const ease = p * p * (3 - 2 * p);
  const motionStyle = ((): React.CSSProperties => {
    if (motion === 'zoom-in') return { transform: `scale(${1 + 0.12 * ease})` };
    if (motion === 'zoom-out') return { transform: `scale(${1.12 - 0.12 * ease})` };
    if (motion === 'pan-left') return { transform: `scale(1.12) translateX(${6 - 12 * ease}%)` };
    if (motion === 'pan-right') return { transform: `scale(1.12) translateX(${-6 + 12 * ease}%)` };
    return {};
  })();
  const brightnessFilter = brightness < 0.999
    ? { filter: `brightness(${Math.max(0, Math.min(1, brightness)).toFixed(3)})` }
    : {};

  const [aw, ah] = canvasAspect === '9:16' ? [9, 16]
    : canvasAspect === '1:1' ? [1, 1]
    : canvasAspect === '4:3' ? [4, 3]
    : canvasAspect === '3:4' ? [3, 4]
    : [16, 9];
  const scale = stageBox.w > 0 && stageBox.h > 0
    ? Math.min(stageBox.w / aw, stageBox.h / ah)
    : 0;
  const frameW = scale * aw;
  const frameH = scale * ah;

  const captionAlign = (pos: 'top' | 'center' | 'bottom') => {
    if (pos === 'top') return { top: '8%', bottom: 'auto' };
    if (pos === 'center') return { top: '50%', bottom: 'auto', transform: 'translateY(-50%)' };
    return { bottom: '10%', top: 'auto' };
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      background: 'rgba(10,10,14,0.95)', borderRadius: 12,
      border: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden',
      height: '100%',
    }}>
      <div
        ref={stageRef}
        style={{
          flex: 1, position: 'relative', background: '#111',
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          minHeight: 0, overflow: 'hidden',
        }}
      >
        {blobUrl ? (
          <div style={{
            position: 'relative',
            width: frameW || '100%',
            height: frameH || '100%',
            background: '#000',
            overflow: 'hidden',
          }}>
            {isImage ? (
              <img
                ref={imgRef}
                src={blobUrl}
                alt=""
                style={{
                  width: '100%', height: '100%', objectFit: fit, display: 'block',
                  transformOrigin: 'center center', ...motionStyle, ...brightnessFilter,
                }}
              />
            ) : (
              <video
                ref={videoRef}
                src={blobUrl}
                style={{
                  width: '100%', height: '100%', objectFit: fit, display: 'block',
                  transformOrigin: 'center center', ...motionStyle, ...brightnessFilter,
                }}
                onEnded={() => {
                  if (videoRef.current) {
                    const nextT = segmentStartTime + (videoRef.current.duration / segmentSpeed) + 0.01;
                    if (nextT >= totalDuration) {
                      onPlayPause();
                    } else {
                      onTimeUpdate(nextT);
                    }
                  }
                }}
              />
            )}
            {captions && captionsSettings && (
              <div style={{
                position: 'absolute',
                left: 0,
                right: 0,
                display: 'flex',
                justifyContent: 'center',
                pointerEvents: 'none',
                padding: '0 20px',
                ...captionAlign(
                  (captions.find(c => currentTime >= c.startTime && currentTime <= c.endTime)?.position)
                  || captionsSettings.position
                ),
              }}>
                {captions
                  .filter(c => currentTime >= c.startTime && currentTime <= c.endTime)
                  .map(cap => (
                    <div key={cap.id} style={{
                      background: captionsSettings.bgColor
                        ? `${captionsSettings.bgColor}${Math.round(captionsSettings.bgOpacity * 255).toString(16).padStart(2, '0')}`
                        : 'rgba(0,0,0,0.5)',
                      padding: '8px 16px',
                      borderRadius: 8,
                      textAlign: 'center',
                      maxWidth: '80%',
                    }}>
                      <span style={{
                        color: cap.isImpactPhrase ? captionsSettings.impactTextColor : captionsSettings.textColor,
                        fontSize: `min(${cap.isImpactPhrase ? captionsSettings.fontSize * 1.3 : captionsSettings.fontSize}px, calc(90vw / ${Math.max(1, (cap.text || '').length) * 0.55}))`,
                        fontWeight: cap.isImpactPhrase ? 800 : 500,
                        fontFamily: `"${captionsSettings.fontFamily}", system-ui, sans-serif`,
                        lineHeight: 1.2,
                        textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                        display: 'inline-block',
                        whiteSpace: 'nowrap',
                      }}>
                        {cap.text}
                      </span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        ) : (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
            color: 'rgba(255,255,255,0.3)', fontSize: '0.85rem',
          }}>
            <Play size={40} />
            <span>Importe mídia para começar</span>
          </div>
        )}
      </div>

      <div style={{
        padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8,
        borderTop: '1px solid rgba(255,255,255,0.06)', flexShrink: 0,
        background: 'rgba(15,15,20,0.9)',
      }}>
        <button onClick={onPlayPause} style={btnStyle}>
          {isPlaying ? <Pause size={14} /> : <Play size={14} />}
        </button>
        <button onClick={() => handleFrameStep(-1)} style={btnStyle} title="Frame anterior">
          <SkipBack size={12} />
        </button>
        <button onClick={() => handleFrameStep(1)} style={btnStyle} title="Próximo frame">
          <SkipForward size={12} />
        </button>
        <input
          type="range"
          min={0}
          max={totalDuration || 1}
          step={0.01}
          value={currentTime}
          onChange={handleSeek}
          style={{ flex: 1, accentColor: '#6366f1', height: 4, cursor: 'pointer' }}
        />
        <span style={{
          fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)',
          fontFamily: 'monospace', flexShrink: 0, minWidth: 70, textAlign: 'right',
        }}>
          {formatTime(currentTime)} / {formatTime(totalDuration)}
        </span>
        <button onClick={() => setLocalMuted(!localMuted)} style={btnStyle}>
          {localMuted ? <VolumeX size={13} /> : <Volume2 size={13} />}
        </button>
        <input
          type="range" min={0} max={1} step={0.05}
          value={localMuted ? 0 : localVolume}
          onChange={(e) => { setLocalVolume(parseFloat(e.target.value)); setLocalMuted(false); }}
          style={{ width: 50, accentColor: '#6366f1', height: 3 }}
        />
        <button onClick={handleCapture} style={{
          ...btnStyle,
          background: 'rgba(168,85,247,0.2)', borderColor: 'rgba(168,85,247,0.4)',
          color: '#d8b4fe',
        }} title="Capturar frame">
          <Camera size={13} />
        </button>
      </div>

      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
}

const btnStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 6, width: 30, height: 28, color: 'rgba(255,255,255,0.7)',
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0, transition: 'all 0.15s',
};
